// src/components/signup/styles/documentViewerModalStyles.ts
import { SxProps, Theme } from '@mui/material/styles';

type ModalStyles =
  | 'dialogPaper'
  | 'dialogTitle'
  | 'dialogTitleEyebrow'
  | 'dialogTitleText'
  | 'dialogContent'
  | 'dialogScrollArea'
  | 'dialogBodyText'
  | 'dialogActions'
  | 'acceptButton'
  | 'declineButton';

export const useDocumentViewerModalStyles = (
  _theme: Theme
): Record<ModalStyles, SxProps<Theme>> => {
  const accentGradient =
    'linear-gradient(135deg, rgba(14,165,233,0.12) 0%, rgba(15,118,110,0.12) 100%)';
  const glassBackground = 'rgba(255,255,255,0.92)';

  return {
    dialogPaper: {
      borderRadius: { xs: 4, md: 6 },
      background: glassBackground,
      backdropFilter: 'blur(22px)',
      boxShadow: '0 45px 80px -40px rgba(15,23,42,0.55)',
      border: '1px solid rgba(226,232,240,0.65)',
      overflow: 'hidden',
    },
    dialogTitle: {
      px: { xs: 3, md: 5 },
      pt: { xs: 3, md: 4 },
      pb: { xs: 2, md: 3 },
      background: accentGradient,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    dialogTitleEyebrow: {
      fontWeight: 700,
      letterSpacing: 3,
      textTransform: 'uppercase',
      color: '#0f766e',
    },
    dialogTitleText: {
      fontWeight: 800,
      color: '#0f172a',
      fontSize: { xs: '1.75rem', md: '2rem' },
      lineHeight: 1.1,
    },
    dialogContent: {
      px: { xs: 3, md: 5 },
      pt: { xs: 3, md: 4 },
      pb: { xs: 3, md: 4 },
      background: 'linear-gradient(180deg, rgba(248,250,252,0.94) 0%, rgba(255,255,255,0.96) 100%)',
    },
    dialogScrollArea: {
      maxHeight: { xs: '55vh', md: '60vh' },
      overflowY: 'auto',
      pr: { xs: 1.5, md: 2 },
      mr: { xs: -1.5, md: -2 },
      '&::-webkit-scrollbar': {
        width: 6,
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 999,
        background: 'rgba(15,118,110,0.35)',
      },
    },
    dialogBodyText: {
      whiteSpace: 'pre-wrap',
      color: '#0f172a',
      fontSize: '1rem',
      lineHeight: 1.75,
    },
    dialogActions: {
      px: { xs: 3, md: 5 },
      pb: { xs: 3, md: 4 },
      pt: 2,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 2,
      background: 'rgba(248,250,252,0.85)',
      borderTop: '1px solid rgba(226,232,240,0.6)',
    },
    acceptButton: {
      borderRadius: '999px',
      px: 4,
      py: 1.25,
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '1rem',
      background: 'linear-gradient(135deg, #0f766e 0%, #0ea5e9 100%)',
      boxShadow: '0 18px 36px -18px rgba(14,165,233,0.5)',
      '&:hover': {
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0f766e 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 22px 40px -20px rgba(14,165,233,0.6)',
      },
    },
    declineButton: {
      borderRadius: '999px',
      px: 4,
      py: 1.25,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '1rem',
      borderColor: '#cbd5e1',
      color: '#0f172a',
      backgroundColor: '#ffffff',
      '&:hover': {
        borderColor: '#94a3b8',
        backgroundColor: '#f8fafc',
      },
    },
  };
};
